import AsyncStorage from "@react-native-async-storage/async-storage";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "./translations.js";

const STORAGE_KEY = "app_locale_v1";

/** @typedef {'en'|'fr'} AppLocale */

const LanguageContext = createContext({
  locale: /** @type {AppLocale} */ ("en"),
  setLocale: /** @type {(l: AppLocale) => void} */ (() => {}),
  t: /** @type {(key: string) => string} */ ((k) => k),
});

/**
 * @param {{ children: React.ReactNode }} props
 */
export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(/** @type {AppLocale} */ ("en"));
  const [ready, setReady] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const raw = await AsyncStorage.getItem(STORAGE_KEY);
        if (raw === "fr" || raw === "en") setLocaleState(raw);
      } catch {
        /* keep default */
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const setLocale = useCallback((next) => {
    const lang = next === "fr" ? "fr" : "en";
    setLocaleState(lang);
    void AsyncStorage.setItem(STORAGE_KEY, lang);
  }, []);

  const t = useCallback(
    (key) => {
      const table = translations[locale] || translations.en;
      return table[key] ?? translations.en[key] ?? key;
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t, ready }), [locale, setLocale, t, ready]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}
